import { useState } from 'react'
import { EventAttendeeState } from 'graphql/generated/scheme'
import { PieImage, RSVPStatus } from 'components/common'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { Button } from 'components/atoms/button'

interface RSVPSuccessModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	attendeeState?: EventAttendeeState | null
	eventAvatarUrl?: string | null
	isGettingFreeTicket?: boolean
	onClose?: () => void
}

export function RSVPSuccessModal({
	open,
	setOpen,
	attendeeState,
	eventAvatarUrl,
	isGettingFreeTicket = false,
	onClose,
}: RSVPSuccessModalProps) {
	const handleClose = () => {
		setOpen(false)
		onClose?.()
	}

	return (
		<ContentModal
			open={open}
			setOpen={setOpen}
			isModal
			hideCloseButton={true}
			title={attendeeState === EventAttendeeState.Approved ? `Your Tickets are Ready!` : `Thank for RSVP'ing!`}
		>
			<div className="relative flex flex-col items-center space-y-5">
				<div className="max-w-[304px]">
					<div className="flex justify-center items-center py-8 relative mb-5">
						<PieImage className="w-[200px] h-auto rounded-xl flex-shrink-0" images={[eventAvatarUrl]} />
						{attendeeState && (
							<div className="absolute bottom-1.5">
								<RSVPStatus state={attendeeState} />
							</div>
						)}
					</div>
					<div>
						{attendeeState === EventAttendeeState.InviteeAccepted && (
							<div className="flex flex-col gap-6">
								<span>We will notify you when your RSVP status changes.</span>
								{!isGettingFreeTicket && (
									<span className="text-sm text-secondary-text italic">
										You will only be charged if you are approved for the event.
									</span>
								)}
							</div>
						)}

						{attendeeState === EventAttendeeState.Approved && (
							<div className="flex flex-col gap-6">
								<span>You're on the list!</span>
								<span className="text-sm text-secondary-text italic">
									You can find your tickets on the top of the event page.
								</span>
							</div>
						)}
					</div>
				</div>
				<Button variant="secondary" fullWidth onClick={handleClose}>
					Close
				</Button>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface RSVPSuccessModalManagedProps {
	attendeeState?: EventAttendeeState | null
	eventAvatarUrl?: string | null
	isGettingFreeTicket?: boolean
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'rsvp-success': RSVPSuccessModalManagedProps
	}
}

export function ManagedRSVPSuccessModal({
	onClose,
	...rest
}: RSVPSuccessModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<RSVPSuccessModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onClose={handleClose}
			{...rest}
		/>
	)
}
